import { useState, useEffect, useRef } from "react";
import { FiSend } from "react-icons/fi";
import { IoMdClose } from "react-icons/io";
import { FaMicrophone, FaMicrophoneSlash } from "react-icons/fa";
import { MdOutlineArrowDropDownCircle } from "react-icons/md";
import "./ChatbotModal.css";
import { LuMinimize2 } from "react-icons/lu";
import { LuMaximize2 } from "react-icons/lu";
import ReactMarkdown from "react-markdown";
import Preview1 from "../Ref_review/Preview1";
import CryptoJS from "crypto-js";

const apiUrl = process.env.REACT_APP_API_URL;
const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;

const ChatbotModal = ({ onClose }) => {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasStartedConversation, setHasStartedConversation] = useState(false);
  const messagesEndRef = useRef(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isAdvanceMode, setIsAdvanceMode] = useState(false);

  const [previewFile, setPreviewFile] = useState(null);
  const [previewName, setPreviewName] = useState("");
  // Log the encryption key for debugging
  console.log("Encryption Key in Frontend:", encryptionKey);

  // Custom encryption function to match backend
  const encryptText = (text, key) => {
    const iv = CryptoJS.lib.WordArray.random(16);
    const keyWordArray = CryptoJS.enc.Utf8.parse(key);
    const encrypted = CryptoJS.AES.encrypt(text, keyWordArray, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    // Prepend IV to ciphertext
    const ivAndCiphertext = iv.concat(encrypted.ciphertext);
    return CryptoJS.enc.Base64.stringify(ivAndCiphertext);
  };

  // Custom decryption function to match backend
  const decryptText = (encryptedText, key) => {
    const ivAndCiphertext = CryptoJS.enc.Base64.parse(encryptedText);
    const iv = ivAndCiphertext.clone();
    iv.sigBytes = 16;
    iv.words = iv.words.slice(0, 4);
    const ciphertext = ivAndCiphertext.clone();
    ciphertext.words = ciphertext.words.slice(4);
    ciphertext.sigBytes = ivAndCiphertext.sigBytes - 16;
    const keyWordArray = CryptoJS.enc.Utf8.parse(key);
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext },
      keyWordArray,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );
    return decrypted.toString(CryptoJS.enc.Utf8);
  };

  useEffect(() => {
    const token = sessionStorage.getItem("currentToken");
    // Load chat history from localStorage
    const savedMessages = JSON.parse(
      localStorage.getItem(`chatHistory_${token}`)
    );
    if (savedMessages) {
      const decryptedMessages = savedMessages.map((msg) => {
        if (msg.text && msg.sender !== "welcome") {
          try {
            const decryptedText = decryptText(msg.text, encryptionKey);
            if (!decryptedText) {
              throw new Error("Decrypted text is empty");
            }
            return { ...msg, text: decryptedText };
          } catch (error) {
            console.error(
              "Failed to decrypt message from localStorage:",
              error
            );
            return { ...msg, text: "Error: Unable to decrypt message" };
          }
        }
        return msg;
      });
      setMessages(decryptedMessages);
    } else {
      setMessages([
        { sender: "welcome", text: "Hello! How can I assist you today?" },
      ]);
    }

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Effect to update the welcome message when advance mode is toggled
  useEffect(() => {
    if (isAdvanceMode) {
      setMessages((prevMessages) => [
        {
          sender: "welcome",
          text: "Advanced search enabled. Responses now include online sources.",
        },
        ...prevMessages.filter((msg) => msg.sender !== "welcome"),
      ]);
    } else {
      setMessages((prevMessages) => [
        { sender: "welcome", text: "Hello! How can I assist you today?" },
        ...prevMessages.filter((msg) => msg.sender !== "welcome"),
      ]);
    }
  }, [isAdvanceMode]);

  useEffect(() => {
    const token = sessionStorage.getItem("currentToken");
    // Save messages to localStorage whenever they change, encrypting non-welcome messages
    const encryptedMessages = messages.map((msg) => {
      if (msg.text && msg.sender !== "welcome") {
        const encryptedText = encryptText(msg.text, encryptionKey);
        return { ...msg, text: encryptedText };
      }
      return msg;
    });
    localStorage.setItem(
      `chatHistory_${token}`,
      JSON.stringify(encryptedMessages)
    );
  }, [messages]);

  useEffect(() => {
    // Scroll to the bottom of the messages container when new messages are added
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const parseResponse = (text, references = [], document_links = []) => {
    const lines = text.split("\n");
    const parsedElements = [];
    let inReferencesSection = false;
    let referenceIndex = 0;

    // Extract filenames from document_links
    const documentLinks = document_links.map((link) => {
      const urlParts = link.split("/");
      return {
        url: link,
        filename: decodeURIComponent(urlParts[urlParts.length - 1]).replace(
          /%20/g,
          " "
        ),
      };
    });

    lines.forEach((line, index) => {
      if (line.startsWith("**References:**")) {
        inReferencesSection = true;
        parsedElements.push(
          <div key="references" className="references">
            <h4>References:</h4>
            {documentLinks.map((doc, i) => (
              <button
                key={`doc-${i}`}
                onClick={() => window.open(doc.url, "_blank")}
                className="reference-button"
              >
                {doc.filename}
              </button>
            ))}
          </div>
        );
        return; // Skip adding the original references line
      }

      if (inReferencesSection && line.startsWith("- ")) {
        // Skip processing reference lines since we added them already
        referenceIndex++;
        return;
      }

      // Existing processing logic for other elements
      if (line.startsWith("## ")) {
        parsedElements.push(
          <h2 key={`heading-${index}`} className="heading">
            {line.replace("## ", "")}
          </h2>
        );
      } else if (line.startsWith("**") && line.endsWith("**")) {
        parsedElements.push(
          <p key={`subheading-${index}`} className="subheading">
            <strong>{line.replace(/\*\*/g, "")}</strong>
          </p>
        );
      } else if (line.startsWith("* ")) {
        // Bullet point with potential nested bold text
        const nestedBoldText = line
          .replace("* ", "")
          .split("**")
          .map((part, i) =>
            i % 2 === 1 ? (
              <strong key={`bold-${index}-${i}`}>{part}</strong>
            ) : (
              part
            )
          );
        parsedElements.push(
          <li key={`bullet-${index}`} className="bullet-point">
            {nestedBoldText}
          </li>
        );
      } else if (line.includes("Download Form")) {
        // Download button
        const match = line.match(/<a href="(.*?)" download>(.*?)<\/a>/);
        if (match) {
          const url = match[1];
          const buttonText = match[2];
          parsedElements.push(
            <div key={`download-${index}`} className="form-response">
              <a
                href={url}
                download
                className="download-button"
                target="_blank"
                rel="noopener noreferrer"
              >
                {buttonText}
              </a>
            </div>
          );
        } else {
          parsedElements.push(
            <p key={`line-${index}`} className="paragraph">
              {line}
            </p>
          );
        }
      } else {
        // Regular paragraph with potential nested bold text
        const nestedBoldText = line
          .split("**")
          .map((part, i) =>
            i % 2 === 1 ? (
              <strong key={`bold-${index}-${i}`}>{part}</strong>
            ) : (
              part
            )
          );
        parsedElements.push(
          <p key={`paragraph-${index}`} className="paragraph">
            {nestedBoldText}
          </p>
        );
      }
    });

    // Add references from advance-chat endpoint if they exist
    if (references.length > 0) {
      parsedElements.push(
        <div key="references" className="references">
          <h4>References:</h4>
          {references.map((ref, i) => (
            <button
              key={`reference-${i}`}
              onClick={() => window.open(ref.link, "_blank")}
              className="reference-button"
            >
              {ref.title}
            </button>
          ))}
        </div>
      );
    }

    return parsedElements;
  };

  const parseChatResponse = (text, document_links = [], form_url = null) => {
    const lines = text.split("\n");
    const parsedElements = [];
    let listItems = [];
    let inReferencesSection = false;
    let formDescription = null; // To store form-related text before "Download Form" button

    lines.forEach((line, index) => {
      // Detect and skip the references section heading
      if (line.startsWith("**References:**")) {
        inReferencesSection = true;
        return;
      }

      // Detect and store form-related text, but prevent duplicate form links
      if (line.includes("Download Form") && form_url) {
        inReferencesSection = false; // Reset
        return; // Skip the redundant download text from response
      } else if (
        line.includes("Ensure to fill this form") ||
        line.includes("must be filled out")
      ) {
        formDescription = line; // Store the form description
        return;
      }

      // Regular text parsing
      if (!inReferencesSection) {
        if (line.startsWith("## ")) {
          parsedElements.push(
            <h2 key={`heading-${index}`} className="heading">
              {line.replace("## ", "")}
            </h2>
          );
        } else if (line.startsWith("**") && line.endsWith("**")) {
          parsedElements.push(
            <p key={`subheading-${index}`} className="subheading">
              <strong>{line.replace(/\*\*/g, "")}</strong>
            </p>
          );
        } else if (line.startsWith("* ")) {
          const cleanedLine = line.replace("* ", "").trim();
          listItems.push(
            <div key={`bullet-${index}`} className="bullet-point">
              {cleanedLine
                .split("**")
                .map((part, i) =>
                  i % 2 === 1 ? <strong key={`bold-${i}`}>{part}</strong> : part
                )}
            </div>
          );
        } else if (line.trim()) {
          parsedElements.push(
            <p key={`para-${index}`} className="paragraph">
              {line
                .split("**")
                .map((part, i) =>
                  i % 2 === 1 ? <strong key={`bold-${i}`}>{part}</strong> : part
                )}
            </p>
          );
        }
      }
    });

    // Add bullet list items if available
    if (listItems.length > 0) {
      parsedElements.push(
        <div key="list-container" className="list-container">
          {listItems}
        </div>
      );
    }

    // Add API-provided document links as references
    if (document_links.length > 0) {
      parsedElements.push(
        <div key="doc-references" className="references">
          <h4>References:</h4>
          {document_links.map((link, i) => {
            const filename = decodeURIComponent(link.split("/").pop()).replace(
              /%20/g,
              " "
            );
            return (
              // <button
              //   key={`doc-${i}`}
              //   onClick={() => window.open(link, "_blank")}
              //   className="reference-button"
              // >
              //   {filename}
              // </button>
              <button
                key={`doc-${i}`}
                onClick={() => {
                  setPreviewFile(link); // Open the specific file in preview
                  setPreviewName(filename);
                }}
                className="reference-button"
              >
                {filename}
              </button>
            );
          })}
        </div>
      );
    }

    // Add Related Form section (if available)
    if (form_url) {
      parsedElements.push(
        <div key="related-form" className="form-section">
          <h4>Related Form:</h4>
          {formDescription && (
            <p key="form-desc" className="form-description">
              {formDescription}
            </p>
          )}
          <a
            href={form_url}
            download
            className="download-button"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download the Form
          </a>
        </div>
      );
    }

    return parsedElements;
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    // Remove "Hello!" message only when user sends the first message
    if (!hasStartedConversation) {
      setHasStartedConversation(true); // Mark conversation as started
    }

    if (!encryptionKey) {
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: "bot", text: "Error: Encryption key is not set" },
      ]);
      return;
    }

    const encryptedInput = encryptText(input, encryptionKey);

    // Retrieve stored details
    const authToken = localStorage.getItem("authToken");
    // const employeeDetails = JSON.parse(localStorage.getItem("employeeDetails"));
    // const userDetails = JSON.parse(localStorage.getItem("userDetails"));

    const employeeDetails = JSON.parse(
      sessionStorage.getItem("currentemployeeDetails")
    );
    const userDetails = JSON.parse(
      sessionStorage.getItem("currentuserDetails")
    );

    setMessages((prevMessages) =>
      prevMessages
        .filter((msg) => msg.sender !== "welcome")
        .concat({ sender: "user", text: input })
    );
    setInput("");
    setIsLoading(true);

    try {
      // Fetch API URL from environment variables
      const apiUrl = process.env.REACT_APP_API_URL;

      // Save query using saveQuery function
      await saveQuery(
        encryptedInput,
        userDetails.id,
        employeeDetails.id,
        employeeDetails.name
      );
      // console.log( userDetails.id);
      // console.log( employeeDetails.id);
      // console.log( employeeDetails.name);

      // Check if advanced mode is enabled
      if (isAdvanceMode) {
        console.log("Advance mode enabled inside handleSend");

        const advanceModeResponse = await fetch(`${apiUrl}/advance-chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: encryptedInput,
            employeeId: employeeDetails.id,
            employeeName: employeeDetails.name,
            companyId: userDetails.id,
          }),
        });

        if (!advanceModeResponse.ok) {
          throw new Error(`HTTP error! Status: ${advanceModeResponse.status}`);
        }

        const advanceModeData = await advanceModeResponse.json();
        setIsLoading(false); // Hide loading animation

        if (!advanceModeData.response) {
          throw new Error("No response received from the server");
        }

        let decryptedResponse;
        try {
          decryptedResponse = decryptText(
            advanceModeData.response,
            encryptionKey
          );
          if (!decryptedResponse) {
            throw new Error("Decrypted response is empty");
          }
        } catch (error) {
          console.error("Decryption failed in advance mode:", error);
          throw new Error("Failed to decrypt the response");
        }

        setMessages((prevMessages) => [
          ...prevMessages,
          {
            sender: "bot",
            text: decryptedResponse,
            references: advanceModeData.references || [],
            isAdvance: true,
          },
        ]);
      } else {
        // Normal chat API request
        const response = await fetch(`${apiUrl}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: encryptedInput,
            employeeId: employeeDetails.id,
            employeeName: employeeDetails.name,
            companyId: userDetails.id,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setIsLoading(false); // Hide loading animation

        if (!data.response) {
          throw new Error("No response received from the server");
        }

        let decryptedResponse;
        try {
          decryptedResponse = decryptText(data.response, encryptionKey);
          if (!decryptedResponse) {
            throw new Error("Decrypted response is empty");
          }
        } catch (error) {
          console.error("Decryption failed in chat mode:", error);
          throw new Error("Failed to decrypt the response");
        }

        setMessages((prevMessages) => [
          ...prevMessages,
          {
            sender: "bot",
            text: decryptedResponse,
            document_links: data.document_links || [],
            form_url: data.form_url || null,
            isChat: true,
          },
        ]);
      }
    } catch (error) {
      console.error("Error in handleSend:", error);
      setIsLoading(false);
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: "bot", text: `Error: ${error.message}` },
      ]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && input.trim()) {
      handleSend();
    }
  };

  const startListening = () => {
    const recognition = new (window.SpeechRecognition ||
      window.webkitSpeechRecognition)();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const speechResult = event.results[0][0].transcript;
      setInput((prevInput) => prevInput + speechResult);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      setIsListening(false);
    };

    recognition.start();
  };

  const suggestions = isAdvanceMode
    ? [
        "How does the SMS address emergency preparedness?",
        "Describe the relationship between SOLAS and ISM Code.",
        "What are OCIMF recommendations for tanker management?",
        "Explain SOLAS Chapter V requirements for navigation safety.",
        "Detail the procedures for handling non-conformities in SMS.",
      ]
    : [
        "Explain hot work permit",
        "Vessel takeover form",
        "Company standing order form",
        "What is master overriding authority",
        "Describe the company's procedures for preventing and responding to oil spills.",
      ];

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion); // Prefill input with suggestion
  };

  const saveQuery = async (queryText, companyId, employeeId, employeeName) => {
    try {
      const response = await fetch(`${apiUrl}/api/queries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyId,
          employeeId,
          employeeName,
          query: {
            queryText,
            timestamp: new Date().toISOString(),
          },
        }),
      });

      if (!response.ok) {
        console.error("Error saving query:", await response.json());
      }
    } catch (error) {
      console.error("Error saving query:", error);
    }
  };

  const toggleMinimize = () => {
    setIsMinimized((prev) => !prev);
  };

  const toggleAdvanceMode = () => {
    setIsAdvanceMode((prev) => !prev);
    console.log("Advance mode enabled inside toggleAdvanceMode");
  };

  return (
    <div
      className={`chatbot-modal ${isMinimized ? "minimized" : ""} ${
        isAdvanceMode ? "advance" : ""
      }`}
    >
      <div className={`chatbot-header ${isAdvanceMode ? "advance" : ""}`}>
        {/* <span className="chatbot-header-title">QMS Chatbot</span> */}
        <span className="chatbot-header-title">AI Shipshape Chatbot</span>
        <div className="chatbot-controls">
          {isMinimized ? (
            <LuMaximize2 className="minimize-btn" onClick={toggleMinimize} />
          ) : (
            <LuMinimize2 className="minimize-btn" onClick={toggleMinimize} />
          )}
          <IoMdClose className="close-icon" onClick={onClose} />
        </div>
      </div>
      <div className={`chatbot-messages ${isAdvanceMode ? "advance" : ""}`}>
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`message ${
              msg.sender === "bot"
                ? "bot"
                : msg.sender === "user"
                ? "user"
                : "welcome"
            } ${isAdvanceMode ? "advance" : ""}`}
          >
            {msg.isChat
              ? parseChatResponse(msg.text, msg.document_links, msg.form_url)
              : parseResponse(
                  msg.text,
                  msg.references || [],
                  msg.document_links || []
                )}
          </div>
        ))}
        {isLoading && (
          <div className="message bot">
            <div className="loading-animation">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      {!hasStartedConversation && (
        <div
          className={`chatbot-suggestions ${isAdvanceMode ? "advance" : ""}`}
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              className={`suggestion-button ${isAdvanceMode ? "advance" : ""}`}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
      <div className={`chatbot-input ${isAdvanceMode ? "advance" : ""}`}>
        <div className="select-icon" onClick={toggleAdvanceMode}>
          <MdOutlineArrowDropDownCircle />
          <span className="tooltip">
            {isAdvanceMode ? "Disable Advance Mode" : "Enable Advance Mode"}
          </span>
        </div>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message..."
        />
        <button onClick={handleSend}>
          <FiSend className="send-icon" />
        </button>
        <button onClick={startListening} className="mic-button">
          {isListening ? (
            <FaMicrophoneSlash className="mic-icon" />
          ) : (
            <FaMicrophone className="mic-icon" />
          )}
        </button>
      </div>
      {previewFile && (
        <Preview1
          fileUrl={previewFile}
          fileName={previewName}
          onClose={() => setPreviewFile(null)} // Close modal when needed
        />
      )}
    </div>
  );
};

export default ChatbotModal;
